import React, { FC, useEffect, useState } from 'react'
import JoditEditor from 'jodit-react'
import { config, getPlainTextLength } from '../../../../reusable'
import { Tr } from '../../../../_metronic/i18n/Tr'
import AddAdvertisementPromotionSelect, {
  PromotionSelection,
} from './AddAdvertisementPromotionSelect'

type DescriptionField = 'mainDescription' | 'descriptionRu' | 'descriptionUz'

interface DetailForm {
  title: string
  titleRu: string
  titleUz: string
  mainDescription: string
  descriptionRu: string
  descriptionUz: string
  galleryImages: File[]
  promoCode: string
}

interface DetailErrors {
  title: string
  titleRu: string
  titleUz: string
  mainDescription: string
  descriptionRu: string
  descriptionUz: string
  galleryImages: string
  promoCode: string
}

interface ExistingMediaImage {
  adsmediaId: number
  adsId: number
  image: string
}

interface AddAdvertisementDetailTabProps {
  detailForm: DetailForm
  detailErrors: DetailErrors
  detailEditorRef: React.RefObject<any>
  promotionSelection: PromotionSelection
  promotionError?: string
  defaultChildIds?: string[]
  existingMediaImages: ExistingMediaImage[]
  imageBaseUrl?: string
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onDescriptionBlur: (newContent: string) => void
  onDescriptionChange: (newContent: string) => void
  onDescriptionRuBlur: (newContent: string) => void
  onDescriptionRuChange: (newContent: string) => void
  onDescriptionUzBlur: (newContent: string) => void
  onDescriptionUzChange: (newContent: string) => void
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveImage: (index: number) => void
  onRemoveExistingImage: (adsmediaId: number) => void
  onPromotionChange: (selection: PromotionSelection) => void
  onPrevious: () => void
  onNext: () => void
}

const AddAdvertisementDetailTab: FC<AddAdvertisementDetailTabProps> = ({
  detailForm,
  detailErrors,
  detailEditorRef,
  promotionSelection,
  promotionError,
  defaultChildIds,
  existingMediaImages,
  imageBaseUrl = '',
  onInputChange,
  onDescriptionBlur,
  onDescriptionChange,
  onDescriptionRuBlur,
  onDescriptionRuChange,
  onDescriptionUzBlur,
  onDescriptionUzChange,
  onFileChange,
  onRemoveImage,
  onRemoveExistingImage,
  onPromotionChange,
  onPrevious,
  onNext,
}) => {
  const t = Tr()
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  // live counts while typing; the form value itself is only committed on blur so the
  // editor isn't re-fed its own content (which resets the cursor)
  const [descLengths, setDescLengths] = useState<Record<DescriptionField, number>>({
    mainDescription: getPlainTextLength(detailForm.mainDescription),
    descriptionRu: getPlainTextLength(detailForm.descriptionRu),
    descriptionUz: getPlainTextLength(detailForm.descriptionUz),
  })

  useEffect(() => {
    setDescLengths({
      mainDescription: getPlainTextLength(detailForm.mainDescription),
      descriptionRu: getPlainTextLength(detailForm.descriptionRu),
      descriptionUz: getPlainTextLength(detailForm.descriptionUz),
    })
  }, [detailForm.mainDescription, detailForm.descriptionRu, detailForm.descriptionUz])

  const trackLength = (field: DescriptionField) => (content: string) =>
    setDescLengths(prev => ({ ...prev, [field]: getPlainTextLength(content) }))

  return (
    <>
      <div className='card card-flush py-4'>
        <div className='card-header d-flex align-items-center'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                <path d='M14 2v6h6M16 13H8M16 17H8M10 9H8' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Advertisement Detail')}</h2>
              <p className='text-muted fs-7 mb-0'>{t('Fill in the advertisement details and content')}</p>
            </div>
          </div>
        </div>
        <div className='card-body pt-0'>
          <form>
            <div className='row'>

              {/* ── Title EN ── */}
              <div className='col-md-4 mb-5'>
<label className='form-label fw-semibold text-gray-700 required'>{t('Title')} </label>
                  <input
                  type='text'
                  className={`form-control ${detailErrors.title ? 'is-invalid' : ''}`}
                  name='title'
                  value={detailForm.title}
                  onChange={onInputChange}
                  maxLength={100}
                  required
                  placeholder={t('Enter advertisement title')}
                />
                {detailErrors.title && (
                  <div className='text-danger mt-1'>{detailErrors.title}</div>
                )}
                <small className='text-muted'>
                  {t('Maximum 100 characters')} ({detailForm.title.length}/100)
                </small>
              </div>

              {/* ── Title Russian ── */}
              <div className='col-md-4 mb-5'>
                <label className='form-label fw-semibold text-gray-700 required'>{t('Title')} (Russian) </label>
                <input
                  type='text'
                  className={`form-control ${detailErrors.titleRu ? 'is-invalid' : ''}`}
                  name='titleRu'
                  value={detailForm.titleRu}
                  onChange={onInputChange}
                  maxLength={100}
                  required
                  placeholder={t('Enter title in Russian')}
                />
                {detailErrors.titleRu && (
                  <div className='text-danger mt-1'>{detailErrors.titleRu}</div>
                )}
                <small className='text-muted'>
                  Maximum 100 characters ({detailForm.titleRu.length}/100)
                </small>
              </div>

              {/* ── Title Uzbek ── */}
              <div className='col-md-4 mb-5'>
                <label className='form-label fw-semibold text-gray-700 required'>{t('Title')} (Uzbek) </label>
                <input
                  type='text'
                  className={`form-control ${detailErrors.titleUz ? 'is-invalid' : ''}`}
                  name='titleUz'
                  value={detailForm.titleUz}
                  onChange={onInputChange}
                  maxLength={100}
                  required
                  placeholder={t('Enter title in Uzbek')}
                />
                {detailErrors.titleUz && (
                  <div className='text-danger mt-1'>{detailErrors.titleUz}</div>
                )}
                <small className='text-muted'>
                  Maximum 100 characters ({detailForm.titleUz.length}/100)
                </small>
              </div>

              {/* ── Main Description EN ── */}
              <div className='col-md-12 mb-5'>
                <label className='form-label fw-semibold text-gray-700 required'>{t('Main Description')} </label>
                <JoditEditor
                  ref={detailEditorRef}
                  config={config}
                  value={detailForm.mainDescription}
                  onBlur={onDescriptionBlur}
                  onChange={trackLength('mainDescription')}
                />
                {detailErrors.mainDescription && (
                  <div className='text-danger mt-1'>{detailErrors.mainDescription}</div>
                )}
                <small className={descLengths.mainDescription > 500 ? 'text-danger' : 'text-muted'}>
                  {t('Maximum 500 characters')} ({descLengths.mainDescription}/500)
                </small>
              </div>

              {/* ── Description Russian ── */}
              <div className='col-md-12 mb-5'>
                <label className='form-label fw-semibold text-gray-700 required'>{t('Main Description')} (Russian) </label>
                <JoditEditor
                  config={config}
                  value={detailForm.descriptionRu}
                  onBlur={onDescriptionRuBlur}
                  onChange={trackLength('descriptionRu')}
                />
                {detailErrors.descriptionRu && (
                  <div className='text-danger mt-1'>{detailErrors.descriptionRu}</div>
                )}
                <small className={descLengths.descriptionRu > 500 ? 'text-danger' : 'text-muted'}>
                  {t('Maximum 500 characters')} ({descLengths.descriptionRu}/500)
                </small>
              </div>

              {/* ── Description Uzbek ── */}
              <div className='col-md-12 mb-5'>
                <label className='form-label fw-semibold text-gray-700 required'>{t('Main Description')} (Uzbek) </label>
                <JoditEditor
                  config={config}
                  value={detailForm.descriptionUz}
                  onBlur={onDescriptionUzBlur}
                  onChange={trackLength('descriptionUz')}
                />
                {detailErrors.descriptionUz && (
                  <div className='text-danger mt-1'>{detailErrors.descriptionUz}</div>
                )}
                <small className={descLengths.descriptionUz > 500 ? 'text-danger' : 'text-muted'}>
                  {t('Maximum 500 characters')} ({descLengths.descriptionUz}/500)
                </small>
              </div>

              {/* ── Promotion Items ── */}
              <AddAdvertisementPromotionSelect
                value={promotionSelection}
                onChange={onPromotionChange}
                error={promotionError}
                defaultChildIds={defaultChildIds}
              />

              {/* ── Gallery Images ── */}
              <div className='col-md-12 mb-5'>
                <label className='form-label fw-semibold text-gray-700 required'>{t('Gallery Images')} </label>
                <div className='mb-3'>
                  <input
                    type='file'
                    className={`form-control ${detailErrors.galleryImages ? 'is-invalid' : ''}`}
                    accept='.jpg,.jpeg,.png,.webp'
                    onChange={onFileChange}
                    multiple
                    required
    
                  />
                  {detailErrors.galleryImages && (
                    <div className='text-danger mt-1'>{detailErrors.galleryImages}</div>
                  )}
                  <small className='text-muted d-block mt-2'>
                    {t('Allowed formats: JPG, PNG, WEBP. Max size per image: 2MB. Maximum 6 images.')}
                  </small>
                </div>

                {/* Existing images from API */}
                {existingMediaImages.length > 0 && (
                  <div className='mt-4'>
                    <h5 className='mb-3'>{t('Uploaded Images')} ({existingMediaImages.length})</h5>
                    <div className='row'>
                      {existingMediaImages.map((media, index) => (
                        <div key={media.adsmediaId} className='col-md-4 mb-3'>
                          <div className='position-relative'>
                            <img
                              src={`${imageBaseUrl}${media.image}`}
                              alt={`Media ${index + 1}`}
                              className='img-fluid rounded'
                              style={{ maxHeight: '90px', minHeight: '90px', width: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type='button'
                              className='btn btn-sm btn-danger position-absolute top-0 end-0 m-2'
                              onClick={() => setConfirmDeleteId(media.adsmediaId)}
                            >
                              <i className='fas fa-trash'></i>
                            </button>
                            <span className='badge text-white bg-success position-absolute bottom-0 start-0 m-2'>
                              {index + 1}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New images pending upload */}
                {detailForm.galleryImages.length > 0 && (
                  <div className='mt-4'>
                    <h5 className='mb-3'>
                      {t('New Images')} ({detailForm.galleryImages.length}/6)
                    </h5>
                    <div className='row'>
                      {detailForm.galleryImages.slice(0, 6).map((image, index) => (
                        <div key={index} className='col-md-4 mb-3'>
                          <div className='position-relative'>
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Preview ${index + 1}`}
                              className='img-fluid rounded'
                              style={{ maxHeight: '90px', minHeight: '90px', width: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type='button'
                              className='btn btn-sm btn-danger position-absolute top-0 end-0 m-2'
                              onClick={() => onRemoveImage(index)}
                            >
                              <i className='fas fa-trash'></i>
                            </button>
                            <span className='badge text-white bg-primary position-absolute bottom-0 start-0 m-2'>
                              {index + 1}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Promo Code ── */}
              <div className='col-md-12 mb-5'>
                <label className='form-label fw-bold'>
                  {t('Promo Code')} <span className='text-muted'>({t('Optional')})</span>
                </label>
                <input
                  type='text'
                  className={`form-control ${detailErrors.promoCode ? 'is-invalid' : ''}`}
                  name='promoCode'
                  value={detailForm.promoCode}
                  onChange={onInputChange}
                  maxLength={20}
                  placeholder={t('Enter promo code (optional)')}
                />
                {detailErrors.promoCode && (
                  <div className='text-danger mt-1'>{detailErrors.promoCode}</div>
                )}
                <small className='text-muted'>
                  {t('Maximum 20 characters')} ({detailForm.promoCode.length}/20)
                </small>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className='d-flex justify-content-end mt-5 gap-3'>
        <button type='button' className='btn btn-secondary' onClick={onPrevious}>
          {t('Previous')}
        </button>
        <button type='button' className='btn btn-primary' onClick={onNext}>
          {t('Next')}
        </button>
      </div>

      {/* ── Delete Confirmation Modal ── */}
      {confirmDeleteId !== null && (
        <div
          className='modal fade show d-block'
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            className='modal-dialog modal-dialog-centered'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{t('Delete Image')}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setConfirmDeleteId(null)}
                />
              </div>
              <div className='modal-body'>
                <p>{t('Are you sure you want to delete this image? This action cannot be undone.')}</p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setConfirmDeleteId(null)}
                >
                  {t('Cancel')}
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={() => {
                    onRemoveExistingImage(confirmDeleteId)
                    setConfirmDeleteId(null)
                  }}
                >
                  {t('Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AddAdvertisementDetailTab
